import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, Lock, User, UserPlus, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function RegisterPage() {
  const { register, auth0Login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register(name, email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white font-['Outfit'] tracking-tight" data-testid="register-title">{t('nav.register')}</h1>
          <p className="text-zinc-500 text-sm mt-2">Create your XDomainX account</p>
        </div>

        <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.name')}</Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <Input value={name} onChange={(e) => setName(e.target.value)} required
                  className="pl-10 h-12 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="register-name-input"
                  placeholder="Full name" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.email')}</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  className="pl-10 h-12 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="register-email-input"
                  placeholder="you@example.com" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">{t('common.password')}</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="pl-10 h-12 bg-[#050505] border-zinc-800 focus:border-[#00E5FF] text-white rounded-sm" data-testid="register-password-input"
                  placeholder="Min 6 characters" />
              </div>
            </div>
            {error && <p className="text-sm text-red-400" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={loading}
              className="w-full h-12 bg-[#00E5FF] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,229,255,0.3)]" data-testid="register-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-2" /> {t('nav.register')}</>}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative flex items-center justify-center">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
              <span className="relative px-4 text-xs text-zinc-600 bg-[#0A0A0A]">or continue with</span>
            </div>
            <Button onClick={auth0Login} variant="outline"
              className="w-full mt-4 h-12 border-zinc-800 hover:border-[#00E5FF] text-zinc-300 hover:text-[#00E5FF] rounded-sm uppercase tracking-wider" data-testid="auth0-register-btn">
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor"><path d="M21.98 7.448L19.62 0H4.347L2.02 7.448c-1.352 4.312.03 9.206 3.815 12.015L12.007 24l6.157-4.552c3.755-2.81 5.182-7.688 3.815-12.015l-1.97-2.41M12.007 17.89l-4.584-3.39L4.7 7.87l2.57-3.39h9.477l2.57 3.39-2.723 6.63z"/></svg>
              Sign up with Auth0
            </Button>
          </div>

          <p className="text-center text-sm text-zinc-500 mt-6">
            Already have an account? <Link to="/login" className="text-[#00E5FF] hover:underline" data-testid="login-link">{t('nav.login')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
